import React from "react";
import { MapRef } from "react-map-gl/maplibre";
import { Geo } from "../../../messages";
import { List } from "../../../utils/List";

export default function useZoomOutToClosestMarker(props: {
  caregiverLocation: Geo;
  markerLocations: List<Geo>;
  mapRef: MapRef | null;
}) {
  const [hasInitiallyZoomedOut, setHasInitiallyZoomedOut] = React.useState(false);

  React.useEffect(() => {
    if (props.mapRef === null || hasInitiallyZoomedOut) {
      return;
    }

    const closestMarker = props.markerLocations
      .map((location) => ({
        location,
        distance: distance(props.caregiverLocation, location),
      }))
      .sortBy(({ distance }) => distance)
      .first();

    if (closestMarker === undefined) {
      return;
    }

    const MIN_ZOOM = 7;
    const MAX_ZOOM = 17;

    const markerLocation = closestMarker.location;
    const caregiverGeo = props.caregiverLocation;

    const initial = {
      center: props.mapRef.getCenter(),
      zoom: props.mapRef.getZoom(),
    };

    props.mapRef.fitBounds(
      [
        [markerLocation.lng, markerLocation.lat],
        [caregiverGeo.lng * 2 - markerLocation.lng, caregiverGeo.lat * 2 - markerLocation.lat],
      ],
      { padding: 60, maxZoom: MAX_ZOOM, animate: false }
    );

    const final = {
      center: props.mapRef.getCenter(),
      zoom: Math.max(props.mapRef.getZoom(), MIN_ZOOM),
    };

    props.mapRef.flyTo({ ...initial, animate: false });
    props.mapRef.flyTo({ ...final, animate: true });

    setHasInitiallyZoomedOut(true);
  }, [hasInitiallyZoomedOut, props.caregiverLocation, props.mapRef, props.markerLocations]);
}

function distance(a: Geo, b: Geo, unit: "K" | "M" = "K"): number {
  const radlat1 = (Math.PI * a.lat) / 180;
  const radlat2 = (Math.PI * b.lat) / 180;
  const theta = a.lng - b.lng;
  const radtheta = (Math.PI * theta) / 180;

  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;

  if (unit == "K") {
    dist = dist * 1.609344;
  }
  if (unit == "M") {
    dist = dist * 0.8684;
  }
  return dist;
}
